import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  Paintbrush, Globe, Mail, Image, Upload, Eye, EyeOff, CheckCircle2,
  Palette, Type, Monitor, Smartphone, Save, RotateCcw, Crown, Lock
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

// ── Types ──────────────────────────────────────────────────────────

interface BrandConfig {
  logoUrl: string;
  faviconUrl: string;
  appName: string;
  tagline: string;
  primaryColor: string;
  accentColor: string;
  backgroundColor: string;
  foregroundColor: string;
  fontFamily: string;
  borderRadius: string;
  customDomain: string;
  domainStatus: "active" | "pending" | "not_configured";
  sslEnabled: boolean;
  poweredByHidden: boolean;
  customLoginPage: boolean;
  loginBgImage: string;
  loginWelcomeText: string;
}

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  enabled: boolean;
  lastModified: string;
}

// ── Seed data ──────────────────────────────────────────────────────

const DEFAULT_BRAND: BrandConfig = {
  logoUrl: "",
  faviconUrl: "",
  appName: "AccouPro",
  tagline: "Smart Accounting Platform",
  primaryColor: "#1a2332",
  accentColor: "#1a2332",
  backgroundColor: "#f8f9fb",
  foregroundColor: "#1a2332",
  fontFamily: "DM Sans",
  borderRadius: "8",
  customDomain: "",
  domainStatus: "not_configured",
  sslEnabled: true,
  poweredByHidden: false,
  customLoginPage: false,
  loginBgImage: "",
  loginWelcomeText: "Welcome back! Sign in to continue.",
};

const EMAIL_TEMPLATES: EmailTemplate[] = [
  { id: "welcome", name: "Welcome Email", subject: "Welcome to {{app_name}}", enabled: true, lastModified: "2026-03-05" },
  { id: "invoice", name: "Invoice Email", subject: "Invoice #{{invoice_number}} from {{company_name}}", enabled: true, lastModified: "2026-03-06" },
  { id: "payment", name: "Payment Confirmation", subject: "Payment received — ₹{{amount}}", enabled: true, lastModified: "2026-03-04" },
  { id: "reminder", name: "Payment Reminder", subject: "Reminder: Invoice #{{invoice_number}} is due", enabled: true, lastModified: "2026-03-03" },
  { id: "gst-filed", name: "GST Filing Confirmation", subject: "GST Return filed for {{period}}", enabled: false, lastModified: "2026-02-28" },
  { id: "password-reset", name: "Password Reset", subject: "Reset your {{app_name}} password", enabled: true, lastModified: "2026-03-01" },
];

const FONT_OPTIONS = ["DM Sans", "Inter", "Roboto", "Poppins", "Nunito", "Lato", "Open Sans", "Source Sans Pro"];

// ── Component ──────────────────────────────────────────────────────

export default function WhiteLabelPage() {
  const { role } = useAuth();
  const isEnterprise = role === "company_admin" || role === "super_admin";

  const [brand, setBrand] = useState<BrandConfig>(DEFAULT_BRAND);
  const [emails, setEmails] = useState<EmailTemplate[]>(EMAIL_TEMPLATES);
  const [previewMode, setPreviewMode] = useState<"desktop" | "mobile">("desktop");
  const [editingTemplate, setEditingTemplate] = useState<string | null>(null);

  const update = (key: keyof BrandConfig, value: string | boolean) => {
    setBrand(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    toast.success("White label settings saved successfully");
  };

  const handleReset = () => {
    setBrand(DEFAULT_BRAND);
    toast.info("Brand settings reset to defaults");
  };

  const toggleEmailTemplate = (id: string) => {
    setEmails(prev => prev.map(e => e.id === id ? { ...e, enabled: !e.enabled } : e));
  };

  const handleDomainConnect = () => {
    if (!brand.customDomain) {
      toast.error("Please enter a domain name");
      return;
    }
    update("domainStatus", "pending");
    toast.success(`Domain verification started for ${brand.customDomain}`);
  };

  if (!isEnterprise) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center space-y-4 px-4">
        <div className="p-4 rounded-full bg-muted"><Crown className="h-8 w-8 text-muted-foreground" /></div>
        <h2 className="text-xl font-bold text-foreground">Enterprise Feature</h2>
        <p className="text-muted-foreground max-w-md">White label customization is available on the Enterprise plan (₹1,999/mo). Upgrade to customize branding, domain, and email templates.</p>
        <Button className="mt-2">Upgrade to Enterprise</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
            <Paintbrush className="h-5 w-5 sm:h-6 sm:w-6 text-primary" /> White Label
          </h1>
          <p className="text-sm text-muted-foreground">Customize branding, domain, and communications for your organization</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleReset}><RotateCcw className="h-3.5 w-3.5 mr-1.5" />Reset</Button>
          <Button size="sm" onClick={handleSave}><Save className="h-3.5 w-3.5 mr-1.5" />Save Changes</Button>
        </div>
      </div>

      <Tabs defaultValue="branding" className="w-full">
        <TabsList className="w-full justify-start overflow-x-auto scrollable-tabs">
          <TabsTrigger value="branding">Branding</TabsTrigger>
          <TabsTrigger value="domain">Custom Domain</TabsTrigger>
          <TabsTrigger value="login">Login Page</TabsTrigger>
          <TabsTrigger value="emails">Email Templates</TabsTrigger>
          <TabsTrigger value="preview">Preview</TabsTrigger>
        </TabsList>

        {/* ── Branding Tab ── */}
        <TabsContent value="branding" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Logo & Identity */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Image className="h-4 w-4" /> Logo & Identity</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Company Logo</Label>
                  <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer">
                    {brand.logoUrl ? (
                      <img src={brand.logoUrl} alt="Logo" className="h-12 mx-auto" />
                    ) : (
                      <div className="space-y-2">
                        <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">Drop logo here or click to upload</p>
                        <p className="text-xs text-muted-foreground">PNG, SVG — max 2MB, recommended 200×60</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Favicon</Label>
                  <div className="border-2 border-dashed border-border rounded-lg p-4 text-center hover:border-primary/50 transition-colors cursor-pointer">
                    <div className="flex items-center justify-center gap-3">
                      <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                        {brand.faviconUrl ? <img src={brand.faviconUrl} alt="Favicon" className="w-6 h-6" /> : <Globe className="h-4 w-4 text-muted-foreground" />}
                      </div>
                      <span className="text-sm text-muted-foreground">Upload favicon (32×32 PNG/ICO)</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Application Name</Label>
                  <Input value={brand.appName} onChange={e => update("appName", e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Tagline</Label>
                  <Input value={brand.tagline} onChange={e => update("tagline", e.target.value)} />
                </div>
              </CardContent>
            </Card>

            {/* Colors & Typography */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Palette className="h-4 w-4" /> Colors & Typography</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { label: "Primary Color", key: "primaryColor" as const },
                  { label: "Accent Color", key: "accentColor" as const },
                  { label: "Background Color", key: "backgroundColor" as const },
                  { label: "Text Color", key: "foregroundColor" as const },
                ].map(c => (
                  <div key={c.key} className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg border border-border overflow-hidden shrink-0">
                      <input type="color" value={brand[c.key]} onChange={e => update(c.key, e.target.value)} className="w-full h-full cursor-pointer border-0" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Label className="text-xs">{c.label}</Label>
                      <Input value={brand[c.key]} onChange={e => update(c.key, e.target.value)} className="h-7 text-xs font-mono mt-0.5" />
                    </div>
                  </div>
                ))}

                <Separator />

                <div className="space-y-2">
                  <Label className="flex items-center gap-1"><Type className="h-3.5 w-3.5" /> Font Family</Label>
                  <Select value={brand.fontFamily} onValueChange={v => update("fontFamily", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FONT_OPTIONS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Border Radius (px)</Label>
                  <Input type="number" value={brand.borderRadius} onChange={e => update("borderRadius", e.target.value)} min="0" max="24" />
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <div>
                    <p className="text-sm font-medium text-foreground">Hide "Powered by" badge</p>
                    <p className="text-xs text-muted-foreground">Remove AccouPro branding from footer</p>
                  </div>
                  <Switch checked={brand.poweredByHidden} onCheckedChange={v => update("poweredByHidden", v)} />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── Custom Domain Tab ── */}
        <TabsContent value="domain" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Globe className="h-4 w-4" /> Custom Domain</CardTitle>
              <CardDescription>Serve AccouPro under your own domain name</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <Label>Domain Name</Label>
                <div className="flex gap-2">
                  <Input value={brand.customDomain} onChange={e => update("customDomain", e.target.value)} placeholder="app.yourcompany.com" className="font-mono" />
                  <Button onClick={handleDomainConnect} disabled={brand.domainStatus === "active"}>
                    {brand.domainStatus === "not_configured" ? "Connect" : brand.domainStatus === "pending" ? "Verifying..." : "Connected"}
                  </Button>
                </div>
              </div>

              <div className="p-4 rounded-lg bg-muted/50 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">Domain Status</span>
                  <Badge variant={brand.domainStatus === "active" ? "default" : brand.domainStatus === "pending" ? "secondary" : "outline"}>
                    {brand.domainStatus === "active" ? <><CheckCircle2 className="h-3 w-3 mr-1" />Active</> : brand.domainStatus === "pending" ? "Verifying DNS" : "Not Configured"}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">SSL Certificate</span>
                  <Badge variant={brand.sslEnabled ? "default" : "outline"}>
                    {brand.sslEnabled ? <><Lock className="h-3 w-3 mr-1" />Enabled</> : "Disabled"}
                  </Badge>
                </div>
              </div>

              {brand.domainStatus !== "active" && (
                <div className="p-4 rounded-lg border border-border space-y-3">
                  <h4 className="text-sm font-semibold text-foreground">DNS Configuration</h4>
                  <p className="text-xs text-muted-foreground">Add these records at your domain registrar:</p>
                  <div className="space-y-2">
                    {[
                      { type: "A", name: "@", value: "185.158.133.1" },
                      { type: "CNAME", name: "www", value: "proxy.accoupro.in" },
                      { type: "TXT", name: "_accoupro", value: "verify=ap_c8f2e91d4a7b" },
                    ].map((r, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 bg-muted/50 rounded text-xs font-mono">
                        <Badge variant="outline" className="text-[10px] w-14 justify-center">{r.type}</Badge>
                        <span className="text-muted-foreground w-20">{r.name}</span>
                        <span className="text-foreground flex-1 truncate">{r.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Login Page Tab ── */}
        <TabsContent value="login" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Custom Login Page</CardTitle>
              <CardDescription>Personalize the login experience for your users</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div>
                  <p className="text-sm font-medium text-foreground">Enable Custom Login Page</p>
                  <p className="text-xs text-muted-foreground">Use your branding on the login screen</p>
                </div>
                <Switch checked={brand.customLoginPage} onCheckedChange={v => update("customLoginPage", v)} />
              </div>

              <div className="space-y-2">
                <Label>Welcome Text</Label>
                <Input value={brand.loginWelcomeText} onChange={e => update("loginWelcomeText", e.target.value)} />
              </div>

              <div className="space-y-2">
                <Label>Background Image</Label>
                <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer">
                  <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                  <p className="text-sm text-muted-foreground mt-2">Upload background image (1920×1080 recommended)</p>
                </div>
              </div>

              {/* Login Preview */}
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="bg-muted/30 p-2 flex items-center justify-between border-b border-border">
                  <span className="text-xs text-muted-foreground">Login Preview</span>
                  <div className="flex gap-1">
                    <Button size="sm" variant={previewMode === "desktop" ? "default" : "ghost"} className="h-6 px-2" onClick={() => setPreviewMode("desktop")}>
                      <Monitor className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant={previewMode === "mobile" ? "default" : "ghost"} className="h-6 px-2" onClick={() => setPreviewMode("mobile")}>
                      <Smartphone className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                <div className={`mx-auto transition-all ${previewMode === "mobile" ? "max-w-[320px]" : "max-w-full"}`}>
                  <div className="p-8 flex flex-col items-center justify-center min-h-[300px]" style={{ backgroundColor: brand.backgroundColor, fontFamily: brand.fontFamily }}>
                    <div className="w-full max-w-sm space-y-6">
                      {brand.logoUrl ? (
                        <img src={brand.logoUrl} alt="Logo" className="h-8 mx-auto" />
                      ) : (
                        <h2 className="text-xl font-bold text-center" style={{ color: brand.foregroundColor }}>{brand.appName}</h2>
                      )}
                      <p className="text-sm text-center" style={{ color: brand.foregroundColor + "99" }}>{brand.loginWelcomeText}</p>
                      <div className="space-y-3">
                        <div className="h-9 rounded bg-white/80 border border-black/10" style={{ borderRadius: `${brand.borderRadius}px` }} />
                        <div className="h-9 rounded bg-white/80 border border-black/10" style={{ borderRadius: `${brand.borderRadius}px` }} />
                        <div className="h-9 rounded flex items-center justify-center text-sm font-medium text-white" style={{ backgroundColor: brand.primaryColor, borderRadius: `${brand.borderRadius}px` }}>
                          Sign In
                        </div>
                      </div>
                      {!brand.poweredByHidden && (
                        <p className="text-[10px] text-center" style={{ color: brand.foregroundColor + "66" }}>Powered by AccouPro</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Email Templates Tab ── */}
        <TabsContent value="emails" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Mail className="h-4 w-4" /> Email Templates</CardTitle>
              <CardDescription>Customize transactional emails sent to your clients. Templates support variables like {"{{app_name}}"}, {"{{company_name}}"}, {"{{invoice_number}}"}.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {emails.map(tmpl => (
                <div key={tmpl.id} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-3 border border-border rounded-lg hover:bg-muted/30 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-foreground">{tmpl.name}</p>
                      <Badge variant={tmpl.enabled ? "default" : "secondary"} className="text-[10px]">{tmpl.enabled ? "Active" : "Disabled"}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground font-mono mt-0.5 truncate">{tmpl.subject}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Modified: {tmpl.lastModified}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={tmpl.enabled} onCheckedChange={() => toggleEmailTemplate(tmpl.id)} />
                    <Button size="sm" variant="outline" className="h-7" onClick={() => setEditingTemplate(editingTemplate === tmpl.id ? null : tmpl.id)}>
                      {editingTemplate === tmpl.id ? "Close" : "Edit"}
                    </Button>
                  </div>
                  {editingTemplate === tmpl.id && (
                    <div className="w-full mt-2 space-y-3 p-3 rounded-lg bg-muted/50">
                      <div className="space-y-1.5">
                        <Label className="text-xs">Subject Line</Label>
                        <Input defaultValue={tmpl.subject} className="text-sm font-mono" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Email Body (HTML)</Label>
                        <textarea
                          className="w-full h-32 p-3 rounded-lg bg-card border border-border text-xs font-mono text-foreground resize-y outline-none focus:ring-1 focus:ring-primary"
                          defaultValue={`<div style="font-family: ${brand.fontFamily}, sans-serif; max-width: 600px; margin: 0 auto;">\n  <div style="background: ${brand.primaryColor}; padding: 24px; text-align: center;">\n    <h1 style="color: #fff; margin: 0;">${brand.appName}</h1>\n  </div>\n  <div style="padding: 32px;">\n    <p>Hello,</p>\n    <p>Your content here...</p>\n  </div>\n  <div style="padding: 16px; text-align: center; color: #999; font-size: 12px;">\n    ${brand.poweredByHidden ? "" : `Powered by ${brand.appName}`}\n  </div>\n</div>`}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => { setEditingTemplate(null); toast.success(`${tmpl.name} template saved`); }}>Save Template</Button>
                        <Button size="sm" variant="outline">Send Test Email</Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Variable Reference */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Template Variables</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  { var: "{{app_name}}", desc: "Your white-label app name" },
                  { var: "{{company_name}}", desc: "Client's company name" },
                  { var: "{{user_name}}", desc: "Recipient's full name" },
                  { var: "{{invoice_number}}", desc: "Invoice reference number" },
                  { var: "{{amount}}", desc: "Formatted amount with currency" },
                  { var: "{{period}}", desc: "GST/report period" },
                  { var: "{{due_date}}", desc: "Payment due date" },
                  { var: "{{action_url}}", desc: "CTA link (verify, reset, etc.)" },
                ].map((v, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 rounded bg-muted/50 text-sm">
                    <code className="text-xs font-mono text-primary bg-primary/5 px-1.5 py-0.5 rounded">{v.var}</code>
                    <span className="text-xs text-muted-foreground">{v.desc}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Preview Tab ── */}
        <TabsContent value="preview" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Brand Preview</CardTitle>
                <div className="flex gap-1">
                  <Button size="sm" variant={previewMode === "desktop" ? "default" : "ghost"} className="h-7 px-2" onClick={() => setPreviewMode("desktop")}>
                    <Monitor className="h-3.5 w-3.5 mr-1" /> Desktop
                  </Button>
                  <Button size="sm" variant={previewMode === "mobile" ? "default" : "ghost"} className="h-7 px-2" onClick={() => setPreviewMode("mobile")}>
                    <Smartphone className="h-3.5 w-3.5 mr-1" /> Mobile
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className={`mx-auto border border-border rounded-lg overflow-hidden transition-all ${previewMode === "mobile" ? "max-w-[375px]" : "max-w-full"}`}>
                {/* Simulated Browser Chrome */}
                <div className="bg-muted p-2 flex items-center gap-2 border-b border-border">
                  <div className="flex gap-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                  </div>
                  <div className="flex-1 bg-background rounded px-3 py-1 text-xs text-muted-foreground font-mono truncate">
                    {brand.customDomain ? `https://${brand.customDomain}/dashboard` : "https://app.accoupro.in/dashboard"}
                  </div>
                </div>

                {/* Simulated App */}
                <div style={{ backgroundColor: brand.backgroundColor, fontFamily: `${brand.fontFamily}, sans-serif` }} className="min-h-[400px]">
                  {/* Sidebar strip */}
                  <div className="flex">
                    <div className="w-14 sm:w-48 min-h-[400px] border-r" style={{ backgroundColor: brand.primaryColor }}>
                      <div className="p-3">
                        {brand.logoUrl ? (
                          <img src={brand.logoUrl} alt="" className="h-6" />
                        ) : (
                          <span className="text-white font-bold text-sm hidden sm:block">{brand.appName}</span>
                        )}
                      </div>
                      <div className="space-y-1 p-2">
                        {["Dashboard", "Ledgers", "Invoices", "GST", "Reports"].map((item, i) => (
                          <div key={item} className={`px-3 py-2 rounded text-xs text-white/80 ${i === 0 ? "bg-white/15 text-white" : "hover:bg-white/10"}`} style={{ borderRadius: `${brand.borderRadius}px` }}>
                            <span className="hidden sm:inline">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Main content */}
                    <div className="flex-1 p-4 sm:p-6 space-y-4">
                      <h1 className="text-lg font-bold" style={{ color: brand.foregroundColor }}>{brand.appName} Dashboard</h1>
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { label: "Revenue", value: "₹4.8L" },
                          { label: "Invoices", value: "156" },
                          { label: "Due", value: "₹82K" },
                          { label: "GST", value: "Filed" },
                        ].map(s => (
                          <div key={s.label} className="p-3 rounded-lg bg-white border" style={{ borderRadius: `${brand.borderRadius}px` }}>
                            <p className="text-xs" style={{ color: brand.foregroundColor + "88" }}>{s.label}</p>
                            <p className="text-lg font-bold" style={{ color: brand.accentColor }}>{s.value}</p>
                          </div>
                        ))}
                      </div>
                      <div className="h-24 rounded-lg bg-white border flex items-center justify-center" style={{ borderRadius: `${brand.borderRadius}px` }}>
                        <span className="text-xs" style={{ color: brand.foregroundColor + "66" }}>Chart Area</span>
                      </div>
                      {!brand.poweredByHidden && (
                        <p className="text-[10px] text-center" style={{ color: brand.foregroundColor + "44" }}>Powered by AccouPro</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
